import { useState, useEffect, useMemo } from 'react';
import './QualitativeCollapsed.scss';
import { useSelector } from 'react-redux';
import * as Sentry from '@sentry/react';
import { Modal } from 'antd';
import DropdownSelect from '../../DropdownSelect/DropdownSelect';
import { t } from '../../../i18n/i18n';
import {
  getCandidateQualitativeIPVPdfExport,
  getCandidateQualitativePdfExport,
} from '../../../API/services/alkymersService';
import HorizontalLoader from '../../HorizontalLoader/HorizontalLoader';
import CloudDownload from '../../../assets/dashboard/CloudDownload';
import { downloadBase64Pdf, downloadFile } from '../../../utils/downloadFile';
import { PromptSubTypes, QualitativeTypes } from '../../../constants/enums';
import { isSFIATestGroup, someSFIATestGroup } from '../../../utils/testgroups';
import closeIcon from '../../../assets/createSearch/close-icon.svg';
import useTranslations from '../../../hooks/useTranslations';

const QualitativeCollapsed = ({
  testgroups,
  recruitSessionId,
  profile,
  screeningInstance,
  setScreeningInstance,
}) => {
  const { key: langKey } = useTranslations();
  const { user } = useSelector((state) => state.auth);
  const [testgroupsToQualitative, setTestgroupsToQualitative] = useState([]);
  const [selectedTestgroup, setSelectedTestgroup] = useState(testgroups[0]);
  const [candidateRole, setCandidateRole] = useState(screeningInstance?.candidateRole ?? '');
  const [modalCandidateRole, setModalCandidateRole] = useState(false);
  const [roleError, setRoleError] = useState('');
  const [reportLoading, setReportLoading] = useState(false);

  const downloadOptions = [
    {
      id: true,
      name: t('DOWNLOAD_REPORT_WITH_AI_AFFINITY'),
    },
    {
      id: false,
      name: t('DOWNLOAD_REPORT_WITHOUT_AI_AFFINITY'),
    },
  ];

  // Constants and validations
  const isIPVTestgroup = () =>
    selectedTestgroup.completeData?.qualitativeConfigResults?.type === QualitativeTypes.IPV;

  const isEITTestgroup = () =>
    selectedTestgroup.completeData?.qualitativeConfigResults?.type === QualitativeTypes.EIT;

  const isSFIATestgroup = selectedTestgroup?.id === PromptSubTypes.SFIA_CORRECTION;

  const sfiaTestgroups = testgroups.filter((tg) => isSFIATestGroup(tg));

  const finishedSFIAScreening =
    isSFIATestgroup &&
    sfiaTestgroups?.every(
      (tg) => tg.results && typeof tg.results.score === 'number' && tg.results.explanation
    );

  const hasIPVTextResults = isIPVTestgroup()
    ? selectedTestgroup?.completeData?.qualitativeConfig?.every((config) => {
        const keys = config?.extremes?.[0]?.shortName?.split(',');
        return (
          keys?.length >= 2 &&
          Boolean(selectedTestgroup?.testGroupQualitativeResults?.result?.[keys[0]]?.[keys[1]])
        );
      })
    : true;

  const hasEITResults = isEITTestgroup()
    ? Boolean(selectedTestgroup?.testGroupQualitativeResults?.eitResult)
    : true;

  const finishedQualitative = Boolean(
    (selectedTestgroup?.testGroupQualitativeResults?.axisResults &&
      hasIPVTextResults &&
      hasEITResults) ||
      finishedSFIAScreening
  );

  const canDownloadReport =
    recruitSessionId &&
    profile?.id &&
    user?.id &&
    finishedQualitative &&
    (isIPVTestgroup() || selectedTestgroup?.id);

  const qualitativeTitle = isSFIATestgroup
    ? t('SKILL_MATCHING_RESULTS_TITLE')
    : selectedTestgroup?.name || '';

  // Effects
  useEffect(() => {
    if (testgroups.length > 0 && someSFIATestGroup(testgroups)) {
      const sfiaTestgroup = {
        id: PromptSubTypes.SFIA_CORRECTION,
        name: t('SKILL_MATCHING_RESULTS_TITLE'),
        completeData: {
          qualitativeConfig: [],
          qualitativeConfigResults: {},
        },
        testGroupQualitativeResults: {},
        tests: [],
      };
      let testgroupsModified;
      if (testgroups.length === 1) {
        testgroupsModified = [sfiaTestgroup];
      } else {
        const filteredTestgroups = testgroups.filter((tg) => !isSFIATestGroup(tg));
        testgroupsModified = [...filteredTestgroups, sfiaTestgroup];
      }
      setTestgroupsToQualitative(testgroupsModified);
      setSelectedTestgroup(testgroupsModified[0]);
    } else {
      setTestgroupsToQualitative(testgroups);
    }
  }, [testgroups]);

  // Functions
  const generateQualitativeReport = async (withAI, candidateRole = null) => {
    setReportLoading(true);
    try {
      let res = null;
      if (isIPVTestgroup()) {
        res = await getCandidateQualitativeIPVPdfExport({
          recruitSessionId,
          profileId: encodeURI(profile?.id),
          partnerId: user?.id,
        });
        const filename = `${profile?.firstName?.toLowerCase() || 'candidate'}-${
          profile?.lastName?.toLowerCase() || 'report'
        }.pdf`;
        const blob = new Blob([res], { type: 'application/pdf' });
        downloadFile(blob, filename);
      } else if (isEITTestgroup()) {
        res = await getCandidateQualitativePdfExport({
          recruitSessionId,
          profileId: encodeURI(profile?.id),
          testgroupId: selectedTestgroup?.id,
          demo: false,
          withAI: false,
          candidateRole: null,
        });
        const filename = `${profile?.firstName?.toLowerCase() || 'candidate'}-${
          profile?.lastName?.toLowerCase() || 'report'
        }-eit.pdf`;
        if (Array.isArray(res) && res.length > 0) {
          downloadBase64Pdf(res[0], filename);
        } else if (res) {
          downloadBase64Pdf(res, filename);
        }
      } else {
        res = await getCandidateQualitativePdfExport({
          recruitSessionId,
          profileId: encodeURI(profile?.id),
          testgroupId: selectedTestgroup?.id,
          demo: false,
          withAI,
          candidateRole: candidateRole ?? null,
        });
        res?.forEach((base64String, index) => {
          const filename = `${profile?.firstName?.toLowerCase() || 'candidate'}-${
            profile?.lastName?.toLowerCase() || 'report'
          }-qualitative-${index + 1}.pdf`;
          downloadBase64Pdf(base64String, filename);
        });
      }
    } catch (error) {
      Sentry.captureException(error, {
        tags: {
          component: 'QualitativeCollapsed',
          action: 'generateQualitativeReport',
        },
        extra: {
          isIPV: isIPVTestgroup(),
          testgroupId: selectedTestgroup?.id,
          profileId: profile?.id,
        },
      });
    } finally {
      setReportLoading(false);
    }
  };

  const handleSelectTestgroup = (tg) => {
    setSelectedTestgroup(testgroupsToQualitative.find((item) => item.id === tg.id));
  };

  const addCandidateRole = async () => {
    if (!screeningInstance?.candidateRole) {
      setScreeningInstance({ ...screeningInstance, candidateRole });
    }
    setModalCandidateRole(false);
    await generateQualitativeReport(true, screeningInstance?.candidateRole ?? candidateRole);
  };

  const selectDownloadQualitativeReportOption = async (withAI) => {
    if (withAI) {
      if (screeningInstance?.candidateRole) {
        await generateQualitativeReport(true, screeningInstance.candidateRole);
      } else {
        document.activeElement?.blur();
        setCandidateRole('');
        setModalCandidateRole(true);
      }
    } else {
      await generateQualitativeReport(false);
    }
  };

  const calculateRangeWidth = (section) => {
    const availableWidth = 200;
    const { minPercent, maxPercent } = section;
    const differenceBetweenPercents = Math.abs(minPercent - maxPercent);

    return `${(differenceBetweenPercents * 100) / availableWidth}%`;
  };

  const calculatePointPosition = (bar) => {
    if (!selectedTestgroup?.testGroupQualitativeResults?.axisResults) {
      return 0;
    }

    const result = selectedTestgroup.testGroupQualitativeResults.axisResults.find(
      (result) => result?.axisId === bar?.id
    );

    if (!result || !result.ratio) {
      return 0;
    }

    const parsedRatio = parseFloat(result.ratio);

    if (Number.isNaN(parsedRatio)) {
      return 0;
    }

    if (isIPVTestgroup()) {
      return parsedRatio * 100;
    }
    return (parsedRatio + 1) * 50;
  };
  const getQualitativeResult = (sectionId, configId) => {
    if (!selectedTestgroup?.testGroupQualitativeResults?.axisResults) {
      return false;
    }

    const foundResult = selectedTestgroup.testGroupQualitativeResults.axisResults.find(
      (result) => result?.axisId === configId
    );

    return sectionId === foundResult?.resultId;
  };

  const qualitativeResultsText = (config) => {
    let text = null;
    if (isIPVTestgroup()) {
      const keys = config?.extremes?.[0]?.shortName?.split(',');
      text =
        selectedTestgroup?.testGroupQualitativeResults?.result?.[`${keys?.[0]}`]?.[`${keys?.[1]}`];
    } else {
      const axisData = selectedTestgroup?.testGroupQualitativeResults?.axisResults?.find(
        (axis) => axis?.axisId === config?.id
      );
      const resultObj = config?.results?.find((result) => result?.id === axisData?.resultId);
      text = resultObj?.text;
    }
    return text?.replace('<:name>', profile?.firstName) || '';
  };

  const mapScoreToPercent = (score) => {
    if (typeof score !== 'number') return 0;
    const min = 0;
    const max = 7;
    if (score < min) return 0;
    if (score > max) return 100;
    return ((score - min) / (max - min)) * 95.5;
  };

  const mapEITScoreToPosition = (score) => {
    if (typeof score !== 'number') return 0;
    const min = 65;
    const max = 134;
    if (score < min) return 0;
    if (score > max) return 100;
    return ((score - min) / (max - min)) * 100;
  };

  const getEITScoreInterpretation = (score) => {
    if (score < 70) return { level: t('EIT_LEVEL_VERY_LOW'), className: 'eit-score-very-low' };
    if (score < 80) return { level: t('EIT_LEVEL_LOW'), className: 'eit-score-low' };
    if (score < 90)
      return { level: t('EIT_LEVEL_BELOW_AVERAGE'), className: 'eit-score-below-average' };
    if (score < 110) return { level: t('EIT_LEVEL_AVERAGE'), className: 'eit-score-average' };
    if (score < 120)
      return { level: t('EIT_LEVEL_ABOVE_AVERAGE'), className: 'eit-score-above-average' };
    if (score < 130) return { level: t('EIT_LEVEL_HIGH'), className: 'eit-score-high' };
    return { level: t('EIT_LEVEL_VERY_HIGH'), className: 'eit-score-very-high' };
  };

  const getEITScoreRange = (score) => {
    if (score > 130) return 'ABOVE_130';
    if (score >= 115 && score <= 129) return '115_TO_129';
    if (score >= 86 && score <= 114) return '86_TO_114';
    if (score >= 70 && score <= 85) return '70_TO_85';
    return 'BELOW_70';
  };

  const getEITScoreDescription = (category, score) => {
    const range = getEITScoreRange(score);
    const translationKey = `EIT_TEXT_${category}_${range}`;
    const firstName = profile?.firstName || '';
    return t(translationKey)
      .replace('{name}', firstName)
      .replace('{standard_score}', score.toString());
  };

  const getTestType = (testgroup) => {
    if (!testgroup) return 'GENERIC';
    if (testgroup?.completeData?.qualitativeConfigResults?.type === QualitativeTypes.EIT)
      return 'EIT';
    if (testgroup?.completeData?.qualitativeConfigResults?.type === QualitativeTypes.IPV)
      return 'IPV';
    if (testgroup?.id === PromptSubTypes.SFIA_CORRECTION) return 'SFIA';
    return 'GENERIC';
  };

  const prepareBarsData = (config) => {
    if (!config?.results || !Array.isArray(config.results)) {
      return [];
    }

    return config.results.map((section, index) => ({
      id: section?.id || `section-${index}`,
      width: calculateRangeWidth(section) || '0%',
      isFirst: index === 0,
      isLast: index === config.results.length - 1,
      colorIndex: index,
      isSelected: Boolean(getQualitativeResult(section?.id, config?.id)),
    }));
  };

  const prepareEITData = () => {
    const eitResult = selectedTestgroup?.testGroupQualitativeResults?.eitResult;
    const zScores = eitResult?.scores?.zScores || {};
    const qualitativeConfig = selectedTestgroup?.completeData?.qualitativeConfig || [];

    const mainCategories = ['CEIA', 'CEIE', 'CEAD', 'CEME', 'CEAG'];

    const categories = qualitativeConfig
      .filter((config) => mainCategories.includes(config.id))
      .map((config) => ({
        id: config.id,
        name: t(`EIT_CATEGORY_NAME_${config.id}`),
        score: zScores?.[config.id] || 0,
        position: mapEITScoreToPosition(zScores?.[config.id] || 0),
      }));

    return {
      type: 'EIT',
      validation: eitResult?.validation,
      totalScore: {
        value: zScores?.EMOTIONAL_QUOTIENT || 0,
        position: mapEITScoreToPosition(zScores?.EMOTIONAL_QUOTIENT || 0),
        interpretation: getEITScoreInterpretation(zScores?.EMOTIONAL_QUOTIENT || 0),
      },
      categories,
    };
  };

  const prepareIPVData = () => ({
    type: 'IPV',
    configs: (selectedTestgroup?.completeData?.qualitativeConfig || []).map((config) => ({
      id: config?.id || '',
      name: config?.name || '',
      pointPosition: calculatePointPosition(config) || 0,
      resultText: qualitativeResultsText(config) || '',
    })),
  });

  const prepareSFIAData = () => ({
    type: 'SFIA',
    testgroups: sfiaTestgroups.map((tg, index) => ({
      id: tg?.id || `sfia-${index}`,
      name: tg?.name || '',
      score: tg?.results?.score || 1,
      pointPosition: mapScoreToPercent(tg?.results?.score || 1),
      explanation: tg?.results?.explanation || '',
      colorIndex: index,
    })),
  });

  const prepareGenericData = () => ({
    type: 'GENERIC',
    configs: (selectedTestgroup?.completeData?.qualitativeConfig || []).map((config) => ({
      id: config?.id || '',
      extremes: (config?.extremes || []).map((extreme) => ({
        id: extreme?.id || '',
        name: extreme?.name || '',
        isSelected: Boolean(
          selectedTestgroup?.completeData?.qualitativeConfigResults &&
            getQualitativeResult(extreme?.id, config?.id)
        ),
      })),
      bars: prepareBarsData(config),
      pointPosition: calculatePointPosition(config) || 0,
      resultText: qualitativeResultsText(config) || '',
    })),
  });

  const prepareQualitativeData = () => {
    if (!finishedQualitative) {
      return { type: 'EMPTY', message: t('QUALITATIVE_TEST_EMPTY_STATE') };
    }

    const testType = getTestType(selectedTestgroup);

    switch (testType) {
      case 'EIT':
        return prepareEITData();
      case 'IPV':
        return prepareIPVData();
      case 'SFIA':
        return prepareSFIAData();
      default:
        return prepareGenericData();
    }
  };

  const ProgressiveBar = ({ position }) => (
    <div className="qualitative-result-bar-wrapper">
      <div className="qualitative-result-bar position-relative b-radius-8">
        <span className="qualitative-result-bar-background b-radius-8" />
        <span
          className="qualitative-result-bar-fill b-radius-8"
          style={{ width: `${position || 0}%` }}
        />
      </div>
      <span
        style={{ left: `${position || 0}%` }}
        data-testid="qualitative-result-bar-point"
        className="qualitative-result-point position-absolute"
      />
    </div>
  );

  const SegmentedBar = ({ bars, pointPosition, configId }) => (
    <div id={configId || ''} className="qualitative-result-bar position-relative mb-3">
      {bars?.map((bar, barIndex) => (
        <span
          key={bar?.id || `bar-${barIndex}`}
          data-testid="progress-bar-section"
          style={{ width: bar?.width || '0%' }}
          className={`qualitative-result-bar ${bar?.isFirst ? 'b-left-radius-8' : ''} ${
            bar?.isLast ? 'b-right-radius-8' : ''
          } ${
            (bar?.colorIndex || 0) % 2
              ? 'qualitative-result-bar-color-1'
              : 'qualitative-result-bar-color-2'
          } ${bar?.isSelected ? 'qualitative-result-bar-color-3' : ''}`}
        />
      ))}
      <span
        style={{ left: `${pointPosition || 0}%` }}
        data-testid="qualitative-result-bar-point"
        className="qualitative-result-point position-absolute"
      />
    </div>
  );

  const EmptyState = ({ message }) => (
    <span data-testid="quantitative-collapsed-empty-state" className="text-xs font-weight-400">
      {message}
    </span>
  );

  const renderInvalidationDetails = (validation) => {
    if (!validation?.reasonParams) return null;

    const { reasonKey, reasonParams, omissionsBySubcategory, inconsistencies } = validation;

    if (reasonKey === 'EIT_TOTAL_OMISSIONS_EXCEEDED' || reasonKey === 'EIT_OMISSIONS_EXCEEDED') {
      const omissionCount = reasonParams?.count ?? 0;

      return (
        <div className="mt-2">
          <p className="mb-1 text-sm">
            <strong>{t('EIT_OMISSIONS_COUNT')}:</strong> {omissionCount}
          </p>
          {omissionsBySubcategory && Object.keys(omissionsBySubcategory).length > 0 && (
            <div className="mt-2">
              <p className="mb-1 text-sm font-weight-600">{t('EIT_OMISSIONS_BY_CATEGORY')}:</p>
              <ul className="mb-0 pl-3">
                {Object.entries(omissionsBySubcategory).map(([category, count]) => (
                  <li key={category} className="text-sm">
                    {t(`EIT_SUBCATEGORY_NAME_${category}`) || category}: {count ?? 0}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      );
    }

    if (reasonKey === 'EIT_INCONSISTENCIES_DETECTED') {
      const inconsistencyScore = reasonParams?.score ?? 0;

      return (
        <div className="mt-2">
          <p className="mb-1 text-sm">
            <strong>{t('EIT_INCONSISTENCIES_SCORE')}:</strong> {inconsistencyScore}
          </p>
          {inconsistencies && Array.isArray(inconsistencies) && inconsistencies.length > 0 && (
            <div className="mt-2">
              <p className="mb-1 text-sm font-weight-600">{t('EIT_INCONSISTENCIES_DETAIL')}:</p>
              <ul className="mb-0 pl-3">
                {inconsistencies.map((inc, index) => (
                  <li key={index} className="text-sm">
                    {t('EIT_QUESTIONS')} {inc?.q1 ?? '?'} {t('EIT_AND')} {inc?.q2 ?? '?'}: {t('EIT_DIFFERENCE')} {inc?.difference ?? 0}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      );
    }

    return null;
  };

  const EITResults = ({ data }) => {
    const isInvalid = data?.validation && !data.validation.isValid;

    return (
      <div className="d-flex flex-column eit-results-container">
        {isInvalid && (
          <div className="alert alert-warning mb-3">
            <strong>{t('EIT_INVALID_TEST')}</strong>
            <p className="mb-1">{t(data.validation?.reasonKey || '')}</p>
            {renderInvalidationDetails(data.validation)}
          </div>
        )}

        {!isInvalid && (
          <>
            <div className="mb-4">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <span className="font-weight-600 text-base">{t('EIT_TOTAL_SCORE')}</span>
                <span className="font-weight-600">{data.totalScore?.value || 0}</span>
              </div>
              <ProgressiveBar position={data.totalScore?.position || 0} />
              <div className="mt-2">
                <p className="text-sm text-light-black mb-0">
                  {getEITScoreDescription('CET', data.totalScore?.value || 0)}
                </p>
              </div>
            </div>

            <div className="d-flex flex-column gap-3">
              <h5 className="mb-2">{t('EIT_CATEGORIES')}</h5>
              {data.categories?.map((category) => (
                <div key={category.id} className="mb-3">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <span className="font-weight-600">{category.name}</span>
                    <span className="font-weight-600">{category.score || 0}</span>
                  </div>
                  <ProgressiveBar position={category.position || 0} />
                  <div className="mt-2">
                    <p className="text-sm text-light-black mb-0">
                      {getEITScoreDescription(category.id, category.score || 0)}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    );
  };

  const SFIAResults = ({ data }) => (
    <div className="d-flex flex-column">
      {data?.testgroups?.map((testgroup, index) => (
        <div key={testgroup?.id || `sfia-${index}`} className="d-flex flex-column gap-1 my-2">
          <p>{testgroup?.name || ''}</p>
          <div className="qualitative-result-bar-wrapper">
            <div
              className={`qualitative-result-bar position-relative b-radius-8 ${
                (testgroup?.colorIndex || 0) % 2
                  ? 'qualitative-result-bar-color-1'
                  : 'qualitative-result-bar-color-2'
              } qualitative-result-bar-color-3`}
              style={{ width: '100%' }}
            />
            <span
              style={{ left: `${testgroup?.pointPosition || 0}%` }}
              data-testid="qualitative-result-bar-point"
              className="qualitative-result-point position-absolute"
            />
          </div>
          <span
            data-testid="qualitative-collapsed-result-description"
            className="font-weight-500 text-base text-light-black"
          >
            {testgroup?.explanation || ''}
          </span>
        </div>
      ))}
    </div>
  );

  const IPVResults = ({ data }) => (
    <div className="d-flex flex-column">
      {data?.configs?.map((config, index) => (
        <div key={config?.id || `config-${index}`} className="my-2">
          <div className="w-100 d-flex justify-content-between mb-2">
            <span
              data-testid="qualitative-bar-0-extreme"
              className="font-weight-600 text-base qualitative-extreme-selected"
            >
              {config?.name || ''}
            </span>
          </div>
          <ProgressiveBar position={config?.pointPosition || 0} />
          {config?.resultText && (
            <div className="mt-2">
              <span
                data-testid="qualitative-collapsed-result-description"
                className="font-weight-500 text-base text-light-black"
              >
                {config?.resultText || ''}
              </span>
            </div>
          )}
        </div>
      ))}
    </div>
  );

  const GenericResults = ({ data }) => (
    <div className="d-flex flex-column">
      {data?.configs?.map((config, index) => (
        <div key={config?.id || `config-${index}`} className="my-2">
          <div className="w-100 d-flex justify-content-between mb-2">
            {config?.extremes &&
              config.extremes.map((extreme, index) => (
                <span
                  key={extreme?.id || `extreme-${index}`}
                  data-testid={`qualitative-bar-${index}-extreme`}
                  className={`font-weight-600 text-base ${
                    extreme?.isSelected ? 'qualitative-extreme-selected' : 'text-light-black'
                  }`}
                >
                  {extreme?.name || ''}
                </span>
              ))}
          </div>

          {config?.bars && config.bars.length > 0 && (
            <SegmentedBar
              bars={config.bars}
              pointPosition={config?.pointPosition || 0}
              configId={config?.id || ''}
            />
          )}

          {config?.resultText && (
            <div>
              <span
                data-testid="qualitative-collapsed-result-description"
                className="font-weight-500 text-base text-light-black"
              >
                {config?.resultText || ''}
              </span>
            </div>
          )}
        </div>
      ))}
    </div>
  );

  const QualitativeResults = ({ data }) => {
    if (!data) return null;

    switch (data?.type) {
      case 'EMPTY':
        return <EmptyState message={data?.message || ''} />;
      case 'EIT':
        return <EITResults data={data} />;
      case 'IPV':
        return <IPVResults data={data} />;
      case 'SFIA':
        return <SFIAResults data={data} />;
      case 'GENERIC':
        return <GenericResults data={data} />;
      default:
        return <GenericResults data={data} />;
    }
  };

  const qualitativeData = useMemo(
    () => prepareQualitativeData(),
    [
      finishedQualitative,
      selectedTestgroup?.id,
      selectedTestgroup?.testGroupQualitativeResults?.axisResults,
      selectedTestgroup?.testGroupQualitativeResults?.eitResult,
      selectedTestgroup?.completeData?.qualitativeConfigResults?.type,
      sfiaTestgroups.length,
    ]
  );

  const isEITValid = () => {
    if (!isEITTestgroup()) return true;
    const eitValidation = selectedTestgroup?.testGroupQualitativeResults?.eitResult?.validation;
    return eitValidation?.isValid !== false;
  };

  // TODO: Remove EIT from this condition when AI affinity for role is developed for EIT reports
  const QualitativeDownloadButton = finishedQualitative && !isSFIATestgroup && isEITValid() && (
    <>
      {isIPVTestgroup() || isEITTestgroup() ? (
        <button
          type="button"
          disabled={reportLoading || !canDownloadReport}
          onClick={() => generateQualitativeReport()}
          className="px-3 py-1 text-xs border-blue-principal cursor-pointer d-flex align-items-center font-weight-700  bg-white border-button"
        >
          {!reportLoading ? (
            <>
              <span className="text-blue-principal  justify-items-center mr-2">
                {t('DOWNLOAD_QUALITATIVE_REPORT')}
              </span>
              <CloudDownload fill="#4987BE" />
            </>
          ) : (
            <HorizontalLoader className="loader-report" />
          )}
        </button>
      ) : (
        <DropdownSelect
          key="download-report-selector"
          options={downloadOptions}
          className="w-100 position-relative d-flex align-items-center"
          selected={null}
          selectedPlaceholder
          placeholderClassname="mr-2 text-xs font-weight-500"
          minWidth="12rem"
          axHeight={false}
          placeholder={
            reportLoading ? (
              <HorizontalLoader className="loader-report ml-4" />
            ) : (
              t('DOWNLOAD_QUALITATIVE_REPORT')
            )
          }
          setState={(e) => {
            if (e?.id !== undefined) selectDownloadQualitativeReportOption(e.id);
          }}
          disabled={reportLoading || !canDownloadReport}
          showDownloadCloud
        />
      )}
    </>
  );

  return (
    <div data-testid="qualitative-collapsed-container" className="d-flex flex-column">
      {testgroupsToQualitative.length <= 1 ? (
        <div className="d-flex justify-content-between py-2 ">
          <span
            data-testid="qualitative-collapsed-testgroup-name"
            className="my-2  text-muted text-base font-weight-600"
          >
            {qualitativeTitle}
          </span>
          {QualitativeDownloadButton}
        </div>
      ) : (
        <div className=" d-flex flex-column flex-sm-row my-2 justify-content-between align-items-end align-items-sm-center">
          <DropdownSelect
            maxHeight={false}
            className="w-100 position-relative d-flex align-items-center"
            setState={handleSelectTestgroup}
            selected={selectedTestgroup.id}
            selectedPlaceholder
            placeholderClassname="mr-2 text-xs font-weight-500"
            minWidth="12rem"
            menuClassnames="translate-options-dropdown"
            options={testgroupsToQualitative.map((tg) => ({
              id: tg.id,
              name: tg?.nameTranslated?.[langKey] || tg.name,
            }))}
            key={selectedTestgroup.id}
          />
          {QualitativeDownloadButton}
        </div>
      )}
      <QualitativeResults data={qualitativeData} />
      <Modal
        className="screening-profile-comment-modal"
        open={modalCandidateRole}
        onCancel={() => {
          setModalCandidateRole(false);
          setCandidateRole('');
        }}
        closeIcon={<img src={closeIcon} alt={t('CLOSE_ALT')} />}
        footer={
          <button
            onClick={addCandidateRole}
            className="rounded-0 font-weight-bold d-flex align-items-center border-0 outline-none px-4 py-2  text-white bg-maya-blue"
            type="button"
            disabled={!screeningInstance?.candidateRole && candidateRole?.length < 10}
          >
            {t('DOWNLOAD_REPORT_MODAL_BUTTON')}
          </button>
        }
      >
        <h3>
          {screeningInstance?.candidateRole
            ? t('SHOW_ROLE_MODAL_TITLE')
            : t('NEW_ROLE_MODAL_TITLE')}
        </h3>
        <input
          id="candidateRole"
          className="rounded p-2 outline-none w-100 mt-4 border candidate-role-input-height text-xs"
          placeholder={t('ADD_ROLE_MODAL_PLACEHOLDER')}
          value={screeningInstance?.candidateRole ?? candidateRole}
          onChange={(e) => {
            const { value } = e.target;

            if (value.length < 50) {
              setCandidateRole(value);
              if (value.trim().length < 10) {
                setRoleError(t('CANDIDATE_ROLE_TOO_SHORT_ERROR'));
              } else {
                setRoleError('');
              }
            }
          }}
          type="text"
          maxLength={50}
          disabled={screeningInstance?.candidateRole}
        />
        {roleError && (
          <p className="danger-text text-xs m-0 mt-1 p-0" data-testid="candidate-role-input-error">
            {roleError}
          </p>
        )}
      </Modal>
    </div>
  );
};

export default QualitativeCollapsed;
